import apiClient from "@/services/apiClient";
import { SEO_ENDPOINTS } from "@/services/endpoints/seo";
import { getAuthToken, getGuestId } from "@/utils/session";

const ADD_TO_CART_SESSION_KEY = "nh-dl-add-to-cart";
const ranScripts = new Set();

export function unwrapDataLayerHtml(payload) {
  if (payload == null) return "";

  if (typeof payload === "string") {
    const trimmed = payload.trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        return unwrapDataLayerHtml(JSON.parse(trimmed));
      } catch {
        return payload;
      }
    }
    return payload;
  }

  if (typeof payload === "object") {
    if (String(payload.status || "").toLowerCase() === "error") {
      console.error(payload.message || "GetDataLayerHtml failed");
      return "";
    }

    const nested =
      payload.data ?? payload.html ?? payload.result ?? payload.content ?? "";
    if (typeof nested === "string") return nested;
    if (nested && typeof nested === "object") return unwrapDataLayerHtml(nested);
  }

  return "";
}

export function extractDataLayerScripts(html) {
  const text = unwrapDataLayerHtml(html);
  if (
    !text ||
    /location\s*\.\s*(reload|replace)|http-equiv\s*=\s*['"]refresh/i.test(text)
  ) {
    return [];
  }

  const blocks = [...text.matchAll(/<script\b[^>]*>([\s\S]*?)<\/script>/gi)]
    .map((match) => String(match[1] || "").trim())
    .filter(Boolean);

  if (blocks.length) return blocks;

  const single = text.replace(/<\/?script\b[^>]*>/gi, "").trim();
  return single ? [single] : [];
}

export function extractDataLayerScript(html) {
  return extractDataLayerScripts(html).join("\n\n");
}

function isolateScript(code) {
  const trimmed = String(code || "").trim();
  if (!trimmed) return "";
  return `;(function(){\n${trimmed}\n})();`;
}

export function readCartIdFromResponse(data) {
  if (data == null || data === "") return 0;

  if (typeof data === "number" && Number.isFinite(data)) {
    return Math.trunc(data);
  }

  if (typeof data === "string") {
    const trimmed = data.trim();
    if (!trimmed) return 0;
    try {
      return readCartIdFromResponse(JSON.parse(trimmed));
    } catch {
      const parsed = parseInt(trimmed, 10);
      return Number.isFinite(parsed) ? parsed : 0;
    }
  }

  if (typeof data === "object") {
    return readCartIdFromResponse(
      data.cartId ?? data.CartId ?? data.id ?? data.tempBookingId
    );
  }

  return 0;
}

export function markAddToCartFired(cartId) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      ADD_TO_CART_SESSION_KEY,
      JSON.stringify({ cartId: String(cartId || ""), at: Date.now() })
    );
  } catch {
    // ignore quota / private-mode errors
  }
}

export function wasAddToCartJustFired(withinMs = 30000) {
  if (typeof window === "undefined") return false;
  try {
    const raw = sessionStorage.getItem(ADD_TO_CART_SESSION_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return Date.now() - Number(parsed?.at || 0) < withinMs;
  } catch {
    return false;
  }
}

export function runDataLayerScript(html) {
  if (typeof window === "undefined") return false;

  const chunks = extractDataLayerScripts(html);
  const code = chunks.join("\n\n");
  if (!code || ranScripts.has(code)) return false;

  ranScripts.add(code);
  window.dataLayer = window.dataLayer || [];

  try {
    chunks.forEach((chunk) => {
      const isolated = isolateScript(chunk);
      if (!isolated) return;
      const script = document.createElement("script");
      script.textContent = isolated;
      document.head.appendChild(script);
      script.remove();
    });
    return true;
  } catch (error) {
    console.error("dataLayer script failed:", error);
    return false;
  }
}

export async function fetchDataLayerHtml(params = {}) {
  const { data } = await apiClient.get(SEO_ENDPOINTS.getDataLayerHtml, {
    params,
  });
  return unwrapDataLayerHtml(data);
}

export async function pushAddToCartDataLayer(cartId) {
  const id = readCartIdFromResponse(cartId);
  if (id <= 0 || typeof window === "undefined") return false;

  try {
    const html = await fetchDataLayerHtml({
      module: "cart",
      events: "add_to_cart",
      slug: "",
      cartId: id,
      token: getAuthToken() || "",
      tempUserId: getGuestId() || "",
    });

    if (!extractDataLayerScript(html)) return false;

    const ran = runDataLayerScript(html);
    if (ran) markAddToCartFired(id);
    return ran;
  } catch (error) {
    console.error("add_to_cart dataLayer failed:", error?.message || error);
    return false;
  }
}
